import { useTranslation } from 'react-i18next';

/**
 * Readiness checklist for the preview ⇄ ready gate (contract §4.2). A family can only be set
 * `ready` once it carries name, tagline, summary, description (EN) and the hero/icon/schematic
 * media. The backend enforces this (422 `required_for_ready`); we mirror it here so a
 * non-technical editor sees, before saving, exactly what's still missing — and why the public
 * page would otherwise render broken.
 */
export interface ReadinessItem {
  /** Stable key matching the server's `required_for_ready` field (e.g. `hero_image_id`). */
  key: string;
  labelKey: string;
  ok: boolean;
}

export function ReadinessChecklist({ items }: { items: ReadinessItem[] }) {
  const { t } = useTranslation();
  const missing = items.filter((i) => !i.ok);
  const allOk = missing.length === 0;

  return (
    <div className="flex flex-col gap-sm rounded-md border-[0.5px] border-hairline bg-canvas-sand p-md">
      <div className="flex items-center justify-between gap-sm">
        <span className="eyebrow text-ink-mute">{t('families.readinessHeading')}</span>
        <span className={allOk ? 'text-small text-signal-teal' : 'text-small text-signal-amber-ink'}>
          {allOk
            ? t('families.readinessReady')
            : t('families.readinessMissing', { count: missing.length })}
        </span>
      </div>
      <ul className="grid grid-cols-1 gap-2xs sm:grid-cols-2">
        {items.map((i) => (
          <li key={i.key} className="flex items-center gap-xs text-small">
            <span aria-hidden className={i.ok ? 'text-signal-teal' : 'text-ink-mute'}>
              {i.ok ? '✓' : '○'}
            </span>
            <span className={i.ok ? 'text-ink-mid' : 'text-ink-deep'}>{t(i.labelKey)}</span>
          </li>
        ))}
      </ul>
      {!allOk && <p className="text-small text-ink-mute">{t('families.readinessHint')}</p>}
    </div>
  );
}
